import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { graphql } from "gatsby";
import PageContainer from "../pageContainer.js";
import styled from "styled-components";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import {
  BsChevronDown,
  BsChevronUp,
  BsClockHistory,
  BsInstagram,
  BsTelephone,
} from "react-icons/bs";
import { IoLocationOutline, IoLogoWhatsapp } from "react-icons/io5";

// import { GoLocation } from "react-icons/go";
// import { GrLocation } from "react-icons/gr";
import { when } from "../../styles/theme.js";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { find } from "lodash";
import { FaFacebookF } from "react-icons/fa";
import { useLocalization } from "gatsby-theme-i18n";
import { Menus } from "./map/menus.js";
import { Reviews } from "./map/reviews.js";
import { NearbyRestaurants } from "./map/nearbyRestaurants.js";
import { ShareWithFriends } from "./map/shareWithFriends.js";

const MarkerPath =
  "M98.616,152.986C90.57,161.81 81.199,169.713 70.676,176.104C69.381,177.054 67.625,177.169 66.185,176.248C50.639,166.359 37.583,154.483 27.32,141.572C13.156,123.809 4.246,104.117 1.194,85.145C-1.915,65.914 0.993,47.403 10.565,32.361C14.336,26.416 19.158,21.003 25.031,16.325C38.533,5.572 53.95,-0.113 69.323,0.002C84.121,0.117 98.731,5.63 111.355,17.16C115.789,21.19 119.517,25.811 122.568,30.82C132.861,47.777 135.077,69.398 130.557,91.306C126.095,112.955 115.011,134.95 98.616,152.943L98.616,152.986ZM66.387,34.203C48.135,34.203 33.337,48.986 33.351,67.238C33.351,85.491 48.135,100.274 66.387,100.274C84.625,100.274 99.422,85.491 99.422,67.238C99.422,49.001 84.639,34.203 66.387,34.203Z";

export const fragment = graphql`
  fragment MapContent on ContentfulPageSectionMap {
    brands {
      name
      facebookLink
      instagramLink
      mapMarkerColor
      restaurants {
        slug
        contentful_id
        name
        address {
          childMarkdownRemark {
            html
          }
        }
        contact
        whatsapp
        showDeliveroo
        deliverooLink
        showFoodpanda
        foodpandaLink
        showOpenrice
        openriceLink
        showKeeta
        keetaLink
        description {
          description
        }
        reviews {
          text
        }
        menuApi {
          menuApi
        }
        nearbyRestaurants {
          restaurant_slug
        }
        mapLocation {
          lat
          lon
        }
        mapLink
        openingHours {
          childMarkdownRemark {
            html
          }
        }
        image {
          description
          gatsbyImageData(layout: CONSTRAINED, width: 240, height: 160)
        }
      }
    }
    internal {
      type
    }
  }
`;

const Container = styled(PageContainer)`
  margin-top: 5rem;
  margin-bottom: 5rem;
`;

const Brands = styled.div`
  ${when("md")} {
    width: 40%;
    /* height: 90vh; */
    padding-right: 2rem;
    overflow-y: auto;
  }
`;

const BrandContainer = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.black};

  &:first-child {
    border-top: none;
  }
`;

const Header = styled.div`
  padding: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

export const ButtonDownIcon = styled(BsChevronDown)`
  display: block;
  // ${when("md")} {
  //   display: none;
  // }
`;

export const ButtonUpIcon = styled(BsChevronUp)`
  display: block;
  // ${when("md")} {
  //   display: none;
  // }
`;

const HeaderIcons = styled.div`
  display: none;

  ${when("md")} {
    display: flex;
    margin-right: 1rem;
  }
`;

const BrandName = styled.h2`
  margin: 0;
  font-size: 2rem;

  ${when("md")} {
    margin-left: 1rem;
  }

  &:lang(en) {
    font-weight: 600;
  }
`;

const BrandHeaderInnerContainer = styled.div`
  display: flex;
  align-items: center;
`;

const BrandIndicator = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: none;

  ${when("md")} {
    display: block;
  }
`;

const Details = styled.div`
  height: ${(props) => (props.isOpened ? "auto" : "0")};
  overflow: hidden;
`;

const IconLink = styled.a`
  display: flex;
  width: 4rem;
  height: 4rem;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.black};
  margin-left: 1rem;

  &:first-child {
    margin-left: 0;
  }

  ${when("md")} {
    border: none;
    margin-left: 0;
  }
`;

const IconLinks = styled.div`
  display: flex;
  margin-top: 1rem;

  ${when("md")} {
    display: none;
  }
`;

const InstagramIcon = styled(BsInstagram)``;
const FacebookIcon = styled(FaFacebookF)``;

const RestaurantListContainer = styled.div`
  display: flex;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const RestaurantListIcon = styled.div`
  width: 4rem;
  margin-top: 0.75rem;
`;

const RestaurantList = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-top: -1rem;
`;

const RestaurantListLink = styled.div`
  height: 4rem;
  line-height: 4rem;
  border-radius: 999px;
  border: 1px solid
    ${(props) =>
      props.isActive ? props.theme.colors.red : props.theme.colors.black};
  background: ${(props) =>
    props.isActive ? props.theme.colors.white : "transparent"};
  color: ${(props) => (props.isActive ? props.theme.colors.red : "inherit")};
  padding: 0 1rem;
  margin-left: 1rem;
  margin-top: 1rem;
  cursor: pointer;
  transition: all 250ms ease-in-out;

  &:hover {
    opacity: 0.8;
  }
`;

const RestaurantDetails = styled.div`
  margin-top: 1rem;
  margin-bottom: 3rem;
`;

const RestaurantDetail = styled.div`
  display: ${(props) => (props.isSelected ? "block" : "none")};
`;

const DetailLine = styled.div`
  display: flex;
  margin-top: 1rem;
  align-items: center;

  &:first-child {
    margin-left: 0;
  }
`;
const DetailIcon = styled.div`
  width: 4rem;
`;

const DetailText = styled.div`
  flex: 1;
`;

const Name = styled.h3`
  margin: 4rem 0 0;
  font-size: 2rem;
  color: ${(props) => props.theme.colors.red};
`;

const H5Title = styled.h4`
  margin-top: 4rem;
  margin-bottom: 0px;
  font-size: 1.8rem;
`;

const LocationIcon = styled(IoLocationOutline)``;
const Location = styled.div`
  margin-right: 3.5rem;
`;

const HoursIcon = styled(BsClockHistory)``;
const Hours = styled.div``;

const PhoneIcon = styled(BsTelephone)``;
const Phone = styled.div``;

const WhatsappIcon = styled(IoLogoWhatsapp)``;
const Whatsapp = styled.div``;

const Deliveries = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-top: 1rem;
`;

const Description = styled.div`
  margin-top: 4rem;
`;

const DeliveryService = styled.div`
  display: block;
  margin-top: 1rem;
  margin-left: 1rem;
  border-radius: 999px;
  overflow: hidden;
`;

const DeliveryServiceLink = styled.a`
  display: block;
  margin-top: 1rem;
  margin-left: 1rem;
  border-radius: 999px;
  overflow: hidden;
`;
const InnerContainer = styled.div`
  display: flex;
`;

const MapImageContainer = styled.div`
  margin-bottom: 1rem;
  border-radius: 1rem;
  overflow: hidden;
`;
const MapImage = styled(GatsbyImage)``;

const MapName = styled.div`
  font-weight: 500;
`;

const MapAddress = styled.div`
  margin-top: 0.5rem;
`;

const MapContainer = styled.div`
  display: none;

  ${when("md")} {
    display: block;
    flex: 1;
    height: 80vh;
    margin-right: ${(props) => props.theme.gutterWidth.md}rem;
  }

  ${when("lg")} {
    margin-right: ${(props) => props.theme.gutterWidth.lg}rem;
  }

  ${when("xl")} {
    margin-right: ${(props) => props.theme.gutterWidth.xl}rem;
  }

  ${when("xxl")} {
    margin-right: ${(props) => props.theme.gutterWidth.xxl}rem;
  }
`;

const Brand = ({
  brand,
  active,
  activeRestaurantIndex,
  setActiveRestaurantIndex,
}) => {
  const containerRef = useRef(null);
  const [isOpened, setIsOpened] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { locale } = useLocalization();
  
  useEffect(() => {    
    if (active) {
      setIsOpened(true);
      setSelectedIndex(activeRestaurantIndex);
    }
  }, [active, activeRestaurantIndex]);

  return (
    <BrandContainer ref={containerRef}>
      <Header
        onClick={() => {
          if (!isOpened) {
            setActiveRestaurantIndex(selectedIndex);
          }
          setIsOpened(!isOpened);
        }}
      >
        <BrandHeaderInnerContainer>
          <BrandIndicator
            style={{ backgroundColor: `#${brand.mapMarkerColor}` }}
          />
          <BrandName>{brand.name}</BrandName>
        </BrandHeaderInnerContainer>
        <BrandHeaderInnerContainer>
          <HeaderIcons>
            {brand.instagramLink && (
              <IconLink href={brand.instagramLink} target={"_blank"}>
                <InstagramIcon size={"1.8rem"} />
              </IconLink>
            )}
            {brand.facebookLink && (
              <IconLink href={brand.facebookLink} target={"_blank"}>
                <FacebookIcon size={"1.8rem"} />
              </IconLink>
            )}
          </HeaderIcons>
          {isOpened ? (
            <ButtonUpIcon size={"2.5rem"} />
          ) : (
            <ButtonDownIcon size={"2.5rem"} />
          )}
        </BrandHeaderInnerContainer>
      </Header>
      <Details isOpened={isOpened}>
        <IconLinks>
          {brand.instagramLink && (
            <IconLink href={brand.instagramLink} target={"_blank"}>
              <InstagramIcon />
            </IconLink>
          )}
          {brand.facebookLink && (
            <IconLink href={brand.facebookLink} target={"_blank"}>
              <FacebookIcon />
            </IconLink>
          )}
        </IconLinks>
        <RestaurantListContainer>
          <RestaurantListIcon>
            <LocationIcon size={"2.5rem"} />
          </RestaurantListIcon>
          <RestaurantList>
            {brand.restaurants.map((restaurant, index) => {
              return (
                <RestaurantListLink
                  key={index}
                  onClick={() => {
                    setSelectedIndex(index);
                    setActiveRestaurantIndex(index);
                  }}
                  isActive={index === selectedIndex}
                >
                  {restaurant.name}
                </RestaurantListLink>
              );
            })}
          </RestaurantList>
        </RestaurantListContainer>
        <RestaurantDetails>
          {brand.restaurants.map((restaurant, index) => {
            const isSelected = selectedIndex === index;
            return (
              <RestaurantDetail key={index} isSelected={isSelected}>
                <Name>{restaurant.name}</Name>
                <DetailLine
                  as={restaurant.mapLink ? "a" : "div"}
                  href={restaurant.mapLink}
                  target={"_blank"}
                >
                  <DetailIcon>
                    <LocationIcon size={"2.5rem"} />
                  </DetailIcon>
                  <DetailText>
                    <Location
                      dangerouslySetInnerHTML={{
                        __html: restaurant.address?.childMarkdownRemark.html,
                      }}
                    />
                  </DetailText>
                </DetailLine>
                <DetailLine>
                  <DetailIcon>
                    <HoursIcon size={"2.5rem"} />
                  </DetailIcon>
                  <DetailText>
                    <Hours
                      dangerouslySetInnerHTML={{
                        __html:
                          restaurant.openingHours?.childMarkdownRemark.html,
                      }}
                    />
                  </DetailText>
                </DetailLine>
                {restaurant.contact && (
                  <DetailLine
                    as={"a"}
                    href={`tel:${restaurant.contact.replace(/[\s]/g, "")}`}
                    target={"_blank"}
                  >
                    <DetailIcon>
                      <PhoneIcon size={"2.5rem"} />
                    </DetailIcon>
                    <DetailText>
                      <Phone>{restaurant.contact}</Phone>
                    </DetailText>
                  </DetailLine>
                )}
                {restaurant.whatsapp && (
                  <DetailLine
                    as={"a"}
                    href={`https://wa.me/${restaurant.whatsapp.replace(
                      /[\s+]/g,
                      ""
                    )}`}
                    target={"_blank"}
                  >
                    <DetailIcon>
                      <WhatsappIcon size={"2.5rem"} />
                    </DetailIcon>
                    <DetailText>
                      <Whatsapp>{restaurant.whatsapp}</Whatsapp>
                    </DetailText>
                  </DetailLine>
                )}
                <H5Title>{locale === "en" ? "Order from our delivery partners" : "從我們的外送合作夥伴訂購"}</H5Title>
                <Deliveries>
                  {restaurant.showFoodpanda &&
                    (restaurant.foodpandaLink ? (
                      <DeliveryServiceLink
                        href={restaurant.foodpandaLink}
                        target={"_blank"}
                      >
                        <StaticImage
                          src="../../images/foodpanda.png"
                          alt="Foodpanda"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryServiceLink>
                    ) : (
                      <DeliveryService>
                        <StaticImage
                          src="../../images/foodpanda.png"
                          alt="Foodpanda"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryService>
                    ))}
                  {restaurant.showDeliveroo &&
                    (restaurant.deliverooLink ? (
                      <DeliveryServiceLink
                        href={restaurant.deliverooLink}
                        target={"_blank"}
                      >
                        <StaticImage
                          src="../../images/deliveroo.png"
                          alt="deliveroo"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryServiceLink>
                    ) : (
                      <DeliveryService>
                        <StaticImage
                          src="../../images/deliveroo.png"
                          alt="deliveroo"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryService>
                    ))}
                  {restaurant.showOpenrice &&
                    (restaurant.openriceLink ? (
                      <DeliveryServiceLink
                        href={restaurant.openriceLink}
                        target={"_blank"}
                      >
                        <StaticImage
                          src="../../images/openrice.png"
                          alt="openrice"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryServiceLink>
                    ) : (
                      <DeliveryService>
                        <StaticImage
                          src="../../images/openrice.png"
                          alt="openrice"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryService>
                    ))}
                  {restaurant.showKeeta &&
                    (restaurant.keetaLink ? (
                      <DeliveryServiceLink
                        href={restaurant.keetaLink}
                        target={"_blank"}
                      >
                        <StaticImage
                          src="../../images/keeta.png"
                          alt="keeta"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryServiceLink>
                    ) : (
                      <DeliveryService>
                        <StaticImage
                          src="../../images/keeta.png"
                          alt="keeta"
                          placeholder="blurred"
                          width={148}
                          height={38}
                        />
                      </DeliveryService>
                    ))}
                </Deliveries>
                <ShareWithFriends restaurant={restaurant} locale={locale} />
                <Description>
                  <p>{restaurant.description?.description}</p>
                </Description>
              </RestaurantDetail>
            );
          })}
        </RestaurantDetails>
      </Details>
    </BrandContainer>
  );
};

const Map = ({ data }) => {
  const containerRef = useRef(null);
  const [activeRestaurant, setActiveRestaurant] = useState(null);
  const [activeBrandIndex, setActiveBrandIndex] = useState(null);
  const [activeRestaurantIndex, setActiveRestaurantIndex] = useState(null);
  const [finalData, setFinalData] = useState(data);
  const { locale } = useLocalization();

  useEffect(() => {
    if (window?.location?.pathname) {
      const path = window.location.pathname.replace(/\/$/, "").split("/");
      const slug = path[path.length - 1];

      // console.log(path);

      if (!slug) {
        return;
      }

      // console.log(finalData);

      for (let i = 0; i < data.brands.length; i++) {
        const brand = data.brands[i];
        for (let j = 0; j < brand.restaurants.length; j++) {
          const restaurant = brand.restaurants[j];
          if (restaurant.slug === slug) {
            setFinalData({
              brands: [
                {
                  ...brand,
                  restaurants: [restaurant],
                },
              ],
            });
            
            const nearbyRestaurantsArr = restaurant.nearbyRestaurants?.map(restaurant => restaurant.restaurant_slug) || [];
            const nearbyRestaurantsObj = [];

            data.brands.forEach(brand => {
              brand.restaurants.forEach(restaurant => {
                if (nearbyRestaurantsArr.includes(restaurant.slug)) {
                  nearbyRestaurantsObj.push(restaurant);
                }
              });
            });

            setActiveBrandIndex(0);
            setActiveRestaurantIndex(0);
            setActiveRestaurant({
              name: restaurant.name,
              address: restaurant.address,
              lat: restaurant.mapLocation.lat,
              lon: restaurant.mapLocation.lon,
              color: brand.mapMarkerColor,
              brandIndex: i,
              restaurantIndex: j,
              image: restaurant.image,
              reviews: restaurant.reviews,
              menuApi: restaurant.menuApi?.menuApi,
              nearbyRestaurants: nearbyRestaurantsObj.length > 0 ? nearbyRestaurantsObj : []
            });
            window.scrollTo(
              0,
              containerRef.current.getBoundingClientRect().top - 30
            );
            return;
          }
        }
      }
    }
  }, []);

  // console.log("found", finalData);

  const restaurantLocations = [];
  for (let i = 0; i < finalData.brands.length; i++) {
    const brand = finalData.brands[i];
    for (let j = 0; j < brand.restaurants.length; j++) {
      const restaurant = brand.restaurants[j];
      restaurantLocations.push({
        name: restaurant.name,
        address: restaurant.address,
        lat: restaurant.mapLocation.lat,
        lon: restaurant.mapLocation.lon,
        color: brand.mapMarkerColor,
        brandIndex: i,
        restaurantIndex: j,
        image: restaurant.image,
      });
    }
  }

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyDzOUqMTala23eXvV944ATF36MfOyCOj5o",
  });

  return (
    <Container as={"section"} ref={containerRef}>
      <InnerContainer>
        <MapContainer>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: "100%",
              }}
              center={{
                lat: 22.351760903380157,
                lng: 114.14115471038522,
              }}
              zoom={11}
              options={{
                styles: [
                  {
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#f5f5f5",
                      },
                    ],
                  },
                  {
                    elementType: "labels.icon",
                    stylers: [
                      {
                        visibility: "off",
                      },
                    ],
                  },
                  {
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#616161",
                      },
                    ],
                  },
                  {
                    elementType: "labels.text.stroke",
                    stylers: [
                      {
                        color: "#f5f5f5",
                      },
                    ],
                  },
                  {
                    featureType: "administrative.land_parcel",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#bdbdbd",
                      },
                    ],
                  },
                  {
                    featureType: "poi",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#eeeeee",
                      },
                    ],
                  },
                  {
                    featureType: "poi",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#757575",
                      },
                    ],
                  },
                  {
                    featureType: "poi.park",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#e5e5e5",
                      },
                    ],
                  },
                  {
                    featureType: "poi.park",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#9e9e9e",
                      },
                    ],
                  },
                  {
                    featureType: "road",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#ffffff",
                      },
                    ],
                  },
                  {
                    featureType: "road.arterial",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#757575",
                      },
                    ],
                  },
                  {
                    featureType: "road.highway",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#dadada",
                      },
                    ],
                  },
                  {
                    featureType: "road.highway",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#616161",
                      },
                    ],
                  },
                  {
                    featureType: "road.local",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#9e9e9e",
                      },
                    ],
                  },
                  {
                    featureType: "transit.line",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#e5e5e5",
                      },
                    ],
                  },
                  {
                    featureType: "transit.station",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#eeeeee",
                      },
                    ],
                  },
                  {
                    featureType: "water",
                    elementType: "geometry",
                    stylers: [
                      {
                        color: "#c9c9c9",
                      },
                    ],
                  },
                  {
                    featureType: "water",
                    elementType: "labels.text.fill",
                    stylers: [
                      {
                        color: "#9e9e9e",
                      },
                    ],
                  },
                ],
              }}
              // onUnmount={onUnmount}
            >
              {restaurantLocations.map((restaurant, index) => {
                return (
                  <Marker
                    key={index}
                    position={{
                      lat: restaurant.lat,
                      lng: restaurant.lon,
                    }}
                    icon={{
                      path: MarkerPath,
                      fillColor: `#${restaurant.color}`,
                      strokeWeight: 0,
                      fillOpacity: 0.9,
                      scale: 0.15,
                      anchor: { x: 66.5, y: 177 },
                    }}
                    onClick={() => {
                      setActiveRestaurant(restaurant);
                      setActiveBrandIndex(restaurant.brandIndex);
                      setActiveRestaurantIndex(restaurant.restaurantIndex);
                    }}
                  />
                );
              })}
              {activeRestaurant && (
                <InfoWindow
                  onCloseClick={() => setActiveRestaurant(null)}
                  position={{
                    lat: activeRestaurant.lat,
                    lng: activeRestaurant.lon,
                  }}
                  options={{
                    pixelOffset: {
                      width: 0,
                      height: -25,
                    },
                  }}
                >
                  <div>
                    {activeRestaurant.image && (
                      <MapImageContainer>
                        <MapImage
                          alt={activeRestaurant.image.description}
                          image={activeRestaurant.image.gatsbyImageData}
                        />
                      </MapImageContainer>
                    )}

                    <MapName>{activeRestaurant.name}</MapName>
                    <MapAddress
                      dangerouslySetInnerHTML={{
                        __html:
                          activeRestaurant.address?.childMarkdownRemark.html,
                      }}
                    />
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          )}
        </MapContainer>
        <Brands>
          {finalData.brands.map((brand, index) => {
            return (
              <Brand
                brand={brand}
                key={index}
                active={activeBrandIndex === index}
                activeRestaurantIndex={activeRestaurantIndex}
                setActiveRestaurantIndex={(restaurantIndex) => {
                  const restaurantLocation = find(restaurantLocations, {
                    restaurantIndex: restaurantIndex,
                    brandIndex: index,
                  });

                  if (restaurantLocation) {
                    setActiveRestaurant(restaurantLocation);
                  }
                }}
              />
            );
          })}
        </Brands>
      </InnerContainer>
        
      {activeRestaurant && activeRestaurant?.reviews && activeRestaurant?.reviews.length > 0 && <Reviews locale={locale} reviews={activeRestaurant?.reviews} />}

      {activeRestaurant && activeRestaurant?.menuApi && <Menus locale={locale} menuApiUrl={activeRestaurant?.menuApi} />}

      {activeRestaurant && activeRestaurant?.nearbyRestaurants && activeRestaurant?.nearbyRestaurants.length > 0 && <NearbyRestaurants locale={locale} nearbyRestaurants={activeRestaurant?.nearbyRestaurants} />}
    </Container>
  );
};

export const mapName = "ContentfulPageSectionMap";

export default Map;
